import { ChevronDown, ChevronUp } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { View, XStack, YStack } from "tamagui";
import { Body } from "../../../../ui/Body";
import { Label } from "../../../../ui/Label";
import { Separator } from "../../../../ui/Separator";
import { Subtext } from "../../../../ui/Subtext";
import { UserAvatar } from "../../../../ui/UserAvatar";
import type ExpenseModuleModel from "../../ExpenseModuleModel";
import type { CalculatorState } from "../Calculator";
import { AddExpenseFormSplitRow } from "./AddExpenseFormSplitRow";
import type { ExpenseFormState } from "./ExpenseFormState";

export const AddExpenseFormSplits: React.FC<{
  module: ExpenseModuleModel;
  calculatorState: CalculatorState;
  formState: ExpenseFormState;
}> = observer(({ module, calculatorState, formState }) => {
  const [expanded, setExpanded] = useState(false);
  const numUsers = module.plan.users.length;

  const usersSplitInfo = formState.usersSplitInfo;

  return (
    <View>
      <YStack
        gap={"$medium"}
        padding="$large"
        backgroundColor={"$backgroundBlock"}
        onPress={() => setExpanded((prevValue) => !prevValue)}
      >
        <XStack alignItems="center" justifyContent="space-between">
          <YStack flex={1} gap="$small">
            <Label>Splits</Label>{" "}
            {usersSplitInfo && (
              <Subtext>
                {usersSplitInfo.equallyDistributed
                  ? `Splitting equally (${module.currencyFormatter.format(
                      usersSplitInfo.equalSplitAmount.toNumber(),
                    )}) between ${usersSplitInfo.count}/${numUsers} users`
                  : `Unequally divided between ${usersSplitInfo.count}/${numUsers} users`}
              </Subtext>
            )}
          </YStack>
          <XStack cursor="pointer" alignItems="center" gap={"$small"}>
            {expanded ? <ChevronUp /> : <ChevronDown />} <Body>Change</Body>
          </XStack>
        </XStack>
      </YStack>
      <Separator noMargins />
      {expanded ? (
        module.plan.users.map((user, index) => {
          return (
            <React.Fragment key={user.uid}>
              {index === 0 ? undefined : <Separator noMargins />}
              <AddExpenseFormSplitRow
                module={module}
                calculatorState={calculatorState}
                formState={formState}
                user={user}
              />
            </React.Fragment>
          );
        })
      ) : (
        <XStack
          alignItems="center"
          padding="$large"
          backgroundColor={"$backgroundBlock"}
          flexWrap="wrap"
          gap={"$large"}
          onPress={() => setExpanded((prevValue) => !prevValue)}
        >
          {module.plan.users.map((user, _index) => {
            const split = formState.splits.find(
              (split) => split.userId === user.uid,
            );
            return (
              <XStack
                opacity={split?.amount.gt(0) ? 1 : 0.5}
                key={user.uid}
                gap={"$medium"}
              >
                <UserAvatar userUid={user.uid} />
                <View>
                  <Subtext>{user.data.displayName}</Subtext>
                  <Subtext>
                    {module.currencyFormatter.format(
                      split?.amount.toNumber() ?? 0,
                    )}
                  </Subtext>
                </View>
              </XStack>
            );
          })}
        </XStack>
      )}
    </View>
  );
});
